import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Button, Form, Input } from 'components/HookForm';
import Message from 'components/Message';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';

import { createSkuSubgroup, linkItemToSubgroup } from '../SkuItems.helpers';
import { getErrorSnackbarDescriptionWithContactLink } from '../SkuErrorSnackbar.helpers';
import { isVisibleSkuEntity } from '../SkuMenuData.normalizers';
import messages from './DuplicateCategoryModal.messages';
import './DuplicateCategoryModal.scss';

const { block, element } = bem('duplicate-category-modal');

export const DUPLICATE_CATEGORY_MODAL_NAME = 'DuplicateCategoryModal';

const createDuplicatedCategory = (venueId, menuId, name) =>
  createSkuSubgroup(venueId, { groupId: menuId, name, enabled: false });

const linkItemsToCategory = async (venueId, categoryId, items) => {
  const results = await Promise.allSettled(
    items.map((item, ordinal) => linkItemToSubgroup(venueId, categoryId, item.id, ordinal))
  );
  return results.filter(r => r.status === 'rejected').length;
};

const showDuplicateResultSnackbar = (dispatch, name, failedCount) => {
  if (failedCount > 0) {
    reportError('Some items failed to link to duplicated SKU category', new Error(`${failedCount} link failures`));
    dispatch(
      createErrorSnackbar(
        messages.categoryDuplicatedWithLinkErrors,
        { categoryName: name, failedCount, strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true }
      )
    );
  } else {
    dispatch(
      createSuccessSnackbar(
        messages.categoryDuplicated,
        { categoryName: name, strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true }
      )
    );
  }
};

const submitDuplicate = async ({ name, venueId, selectedMenu, itemsToLink, dispatch, onSuccess, closeModal }) => {
  let createResponse;
  try {
    createResponse = await createDuplicatedCategory(venueId, selectedMenu.id, name);
  } catch (error) {
    reportError('Error creating SKU category during duplicate', error);
    dispatch(
      createErrorSnackbar(
        messages.categoryDuplicateError,
        { strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true },
        getErrorSnackbarDescriptionWithContactLink(messages.categoryDuplicateErrorDescription)
      )
    );
    return;
  }

  const newCategoryId = createResponse?.subGroup?.id || createResponse?.id;
  const failedCount = await linkItemsToCategory(venueId, newCategoryId, itemsToLink);
  showDuplicateResultSnackbar(dispatch, name, failedCount);

  try {
    await onSuccess?.();
  } catch (error) {
    reportError('Error refreshing after category duplicate', error);
  }
  closeModal();
};

const DuplicateCategoryModal = ({ category, menus, venueId, onSuccess, closeModal }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const enabledMenus = useMemo(() => (menus || []).filter(isVisibleSkuEntity), [menus]);
  const sourceMenu = useMemo(
    () => enabledMenus.find(menu => String(menu.id) === String(category.menuId)) || null,
    [enabledMenus, category.menuId]
  );
  const [selectedMenu, setSelectedMenu] = useState(sourceMenu);

  const sourceItems = category.items || [];
  const [removedItemIds, setRemovedItemIds] = useState(() => new Set());
  const visibleItems = useMemo(
    () => sourceItems.filter(item => !removedItemIds.has(item.id)),
    [sourceItems, removedItemIds]
  );

  const defaultCategoryName = useMemo(
    () => intl.formatMessage(messages.copySuffix, { categoryName: category.name }),
    [intl, category.name]
  );

  const handleRemoveItem = itemId => {
    setRemovedItemIds(prev => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={messages.title} />
        </h3>
        <button
          type="button"
          aria-label={intl.formatMessage(messages.closeAriaLabel)}
          className={element('close-icon')}
          onClick={closeModal}>
          <Isvg src={ICONS.cross} />
        </button>
      </header>
      <Form id="duplicate-category-form" defaultValues={{ categoryName: defaultCategoryName }}>
        {({ formState, handleSubmit, watch }) => {
          const { isSubmitting } = formState;
          const currentName = watch('categoryName');
          const trimmedName = currentName?.trim() || '';
          const canSubmit = Boolean(trimmedName) && Boolean(selectedMenu);

          const handleFormSubmit = () =>
            submitDuplicate({
              name: trimmedName,
              venueId,
              selectedMenu,
              itemsToLink: visibleItems,
              dispatch,
              onSuccess,
              closeModal,
            });

          return (
            <>
              <div className={element('form-fields')}>
                <Input
                  name="categoryName"
                  disabled={isSubmitting}
                  label={messages.categoryNameLabel}
                  placeholder={messages.categoryNamePlaceholder}
                  type="text"
                  required
                  showRequiredLabel
                  validate={{
                    message: messages.categoryNameRequired,
                    value: value => Boolean(value?.trim()),
                  }}
                />
                <div className={element('menu-section')} data-testid="menu-section">
                  <div className={element('menu-label')}>
                    <Message message={messages.selectMenu} />
                    <Message as="span" className={element('required-label')} message={messages.required} />
                  </div>
                  <Dropdown>
                    {() => (
                      <>
                        <DropdownToggle>
                          <span className={element(selectedMenu ? 'dropdown-value' : 'dropdown-placeholder')}>
                            {selectedMenu ? selectedMenu.name : intl.formatMessage(messages.selectMenu)}
                          </span>
                        </DropdownToggle>
                        <DropdownMenu>
                          {enabledMenus.map(menu => (
                            <DropdownItem
                              key={menu.id}
                              isSelected={selectedMenu?.id === menu.id}
                              onClick={() => setSelectedMenu(menu)}>
                              {menu.name}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </>
                    )}
                  </Dropdown>
                </div>
                {sourceItems.length > 0 && (
                  <div className={element('items-section')} data-testid="items-section">
                    <Message as="div" className={element('items-label')} message={messages.itemsLabel} />
                    <ul className={element('items-list')}>
                      {visibleItems.map(item => (
                        <li key={item.id} className={element('item-row')}>
                          <button
                            type="button"
                            className={element('remove-item-btn')}
                            aria-label={intl.formatMessage(messages.removeItemAriaLabel, { itemName: item.name })}
                            onClick={() => handleRemoveItem(item.id)}>
                            <Isvg src={ICONS.cross} />
                          </button>
                          <span className={element('item-name')}>{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <footer className={element('footer')}>
                <Message
                  as={Button}
                  className={element('cancel-btn')}
                  disabled={isSubmitting}
                  onClick={closeModal}
                  message={messages.cancel}
                  styled="primary-outline"
                  type={Button.TYPES.BUTTON}
                />
                <Message
                  as={Button}
                  className={element('save-btn')}
                  disabled={!canSubmit || isSubmitting}
                  onClick={handleSubmit(handleFormSubmit)}
                  message={messages.save}
                  styled="primary"
                  type={Button.TYPES.SUBMIT}
                />
              </footer>
            </>
          );
        }}
      </Form>
    </div>
  );
};

DuplicateCategoryModal.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    menuId: PropTypes.string,
    name: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      enabled: PropTypes.bool,
    })
  ),
  venueId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
};

export default DuplicateCategoryModal;
